import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  useFetchIpv4Groups,
  useRemoveIpv4Group,
  useUpdateIpv4Group,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { Ipv4Group } from '@/shared/interfaces/erp/system-settings';
import { useUiConfirmModalStore } from '@/store/ui';
import { IPv4DetailsCell, ProgressBarIPVGroups } from '../../shared/components';



export type Ipv4GroupsPageProps = {};

const Ipv4GroupsPage: React.FC<Ipv4GroupsPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_grupoipv4);
  const returnUrlIpV4 = ROUTER_PATHS.configuracionRED.grupoIpv4Nav;
  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    cleanFilter,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: ipv4groupsPagingRes,
    isLoading,
    isRefetching,
  } = useFetchIpv4Groups({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const deleteIpv4GroupMutation = useRemoveIpv4Group();
  const updSate = useUpdateIpv4Group();

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Ipv4Group>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 262,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      /* {
        accessorKey: 'codigo',
        header: 'Código',
        size: 111,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */
      {
        accessorKey: 'ipv_4',
        header: 'RED',
        size: 120,
        Cell: ({ row }) => emptyCellOneLevel(row, 'ipv_4'),
      },

      {
        accessorKey: 'uso',
        header: 'Uso IPS',
        size: 267,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <ProgressBarIPVGroups ipDisponibles={row.original?.ips_disponibles} 
          total_ips={row.original?.total_ips} total_ips_used={row.original?.total_ips_used}/>
        ),
      },

      {
        accessorKey: 'detalle',
        header: 'Detalle',
        size: 60,
        Cell: ({ row }) => (
          <IPv4DetailsCell ipv4Group={row.original?.codigo} availableIps={row.original?.ips_disponibles || []} />
        ),
      },

      {
        accessorKey: 'cidr',
        header: 'CIDR',
        size: 60,
        Cell: ({ row }) => emptyCellOneLevel(row, 'cidr'),
      },

      // {
      //   accessorKey: 'router',
      //   header: 'Router',
      //   size: 222,
      // },

      {
        accessorKey: 'tipo_uso',
        header: 'Tipo de Uso',
        size: 111,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_uso'),
      },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={state => {
                if (!hasPermission(PermissionsEnum.api_change_grupoipv4))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_grupoipv4!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (original: Ipv4Group) => {
    navigate(`${returnUrlIpV4}/editar/${original?.id_grupoipv4}`);
  };
  const onDelete = (original: Ipv4Group) => {
    setConfirmDialog({
      isOpen: true,
      subtitle: '¿Está seguro que desea eliminar el Grupo IPv4 seleccionado?',
      title: 'Confirmar eliminación',
      onConfirm: () => {
        deleteIpv4GroupMutation.mutate(original.id_grupoipv4!);
      },
      onClose() {
        setConfirmDialogIsOpen(false);
      },
    });
  };

  return (
    <SingleTableBoxScene
      title="Grupos IPv4"
      urlRedirect={`${returnUrlIpV4}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_grupoipv4)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
        cleanFilter={cleanFilter}
      />

      <GeneralTable<Ipv4Group>
        columns={columns}
        data={ipv4groupsPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={ipv4groupsPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_sector,
          PermissionsEnum.api_view_router,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_sector,
          PermissionsEnum.api_view_router,
        ])}
        onEdit={onEdit}
        canDelete={false}
        onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default Ipv4GroupsPage;
